<template>
  <div class="pub-page mp-page mp-page--wide">
    <SectionHeader section-key="publish">
      <template #actions>
        <el-button @click="$router.push('/data-analytics')">查看数据</el-button>
        <el-button type="primary" :loading="refreshingAccounts" @click="refreshAccounts">刷新账号</el-button>
        <el-dropdown trigger="click">
          <el-button>
            更多操作
            <el-icon class="el-icon--right"><ArrowDown /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item :disabled="batchProgress.active" @click="syncYimeiCookies">
                同步易媒
              </el-dropdown-item>
              <el-dropdown-item :disabled="batchProgress.active" @click="autoLoginAll">
                一键全登录
              </el-dropdown-item>
              <el-dropdown-item :disabled="batchProgress.active" @click="showBatchCreate = true">
                批量建号
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </template>
    </SectionHeader>

    <!-- KPI：点击切到队列并筛选 -->
    <div v-if="tab !== 'sms'" class="kpi-strip">
      <button
        v-for="item in kpiItems"
        :key="item.key"
        type="button"
        class="kpi"
        :class="[{ active: tab === 'jobs' && jobFilter === item.filter }, item.tone]"
        @click="focusJobs(item.filter)"
      >
        <span class="kpi-value">{{ item.value }}</span>
        <span class="kpi-label">{{ item.label }}</span>
      </button>
      <div class="kpi kpi-static">
        <span class="kpi-value">{{ loggedInCount }}</span>
        <span class="kpi-label">可用账号</span>
      </div>
      <div class="kpi kpi-static">
        <span class="kpi-value">{{ successRate }}%</span>
        <span class="kpi-label">成功率</span>
      </div>
    </div>

    <el-alert
      v-if="batchProgress.active"
      type="info"
      :closable="false"
      show-icon
      class="batch-alert"
    >
      <template #title>
        正在批量添加（{{ batchProgress.current }} / {{ batchProgress.total }}）
        <span v-if="batchProgress.accountId">— {{ batchProgress.accountId }}</span>
      </template>
      <el-progress
        :percentage="Math.round((batchProgress.current / Math.max(batchProgress.total, 1)) * 100)"
        :stroke-width="8"
        style="margin-top: 8px"
      />
    </el-alert>

    <el-tabs v-model="tab" class="pub-tabs">
      <!-- ========== 账号 ========== -->
      <el-tab-pane name="accounts">
        <template #label>
          账号 <span class="tab-count">{{ accounts.length }}</span>
        </template>

        <div class="accounts-layout">
          <!-- 平台侧栏 -->
          <aside class="plat-rail">
            <button
              type="button"
              class="plat-item"
              :class="{ active: platformFilter === '' }"
              @click="platformFilter = ''"
            >
              <span>全部平台</span>
              <span class="plat-count">{{ accounts.length }}</span>
            </button>
            <button
              v-for="g in accountsByPlatform"
              :key="g.code"
              type="button"
              class="plat-item"
              :class="{ active: platformFilter === g.code }"
              @click="platformFilter = g.code"
            >
              <span class="plat-name">
                {{ g.name }}
                <el-tag v-if="g.isApiNative" size="small" type="success" effect="plain" class="api-tag">API</el-tag>
              </span>
              <span class="plat-count">{{ g.accounts.length }}</span>
            </button>
          </aside>

          <!-- 主列表 -->
          <div class="accounts-main" v-loading="loadingAccounts">
            <div class="list-toolbar">
              <el-input
                v-model="accountQuery"
                clearable
                placeholder="搜索账号名 / ID"
                style="width: 220px"
              />
              <div class="status-chips">
                <button
                  v-for="c in statusChips"
                  :key="c.key"
                  type="button"
                  class="chip"
                  :class="{ active: healthFilter === c.key }"
                  @click="healthFilter = c.key"
                >
                  {{ c.label }}
                  <span v-if="c.count != null" class="chip-n">{{ c.count }}</span>
                </button>
              </div>
            </div>

            <el-empty
              v-if="!loadingAccounts && !filteredAccounts.length"
              description="没有匹配的账号"
              :image-size="64"
            />

            <div v-else class="account-grid">
              <article
                v-for="row in filteredAccounts"
                :key="`${row.platform}-${row.account_id}`"
                class="acc-card"
                :class="{ disabled: !row.enabled }"
              >
                <div class="acc-card-top">
                  <el-avatar :size="44" :src="avatarSrc(row)">{{ (row.display_name || '?')[0] }}</el-avatar>
                  <div class="acc-card-id">
                    <div class="acc-name">
                      <a
                        v-if="row.homepage_url"
                        class="acc-name-link"
                        :href="row.homepage_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                      >{{ row.display_name || row.account_id }}</a>
                      <template v-else>{{ row.display_name || row.account_id }}</template>
                    </div>
                    <div class="acc-meta">
                      <span>{{ platformName(row.platform) }}</span>
                      <span class="dot">·</span>
                      <span class="mono">{{ row.account_id }}</span>
                    </div>
                    <div
                      v-if="row.platform === 'xiaohongshu' && row.external_user_id"
                      class="acc-uid"
                      :title="'小红书用户 ID（永久业务键）'"
                    >
                      UID {{ row.external_user_id }}
                    </div>
                  </div>
                  <el-switch
                    v-model="row.enabled"
                    size="small"
                    @change="toggleAccount(row)"
                  />
                </div>

                <div class="acc-card-status">
                  <el-tag
                    v-if="row.platform === 'xiaohongshu' && row.lifecycle_readonly"
                    size="small"
                    type="info"
                    effect="dark"
                  >{{ row.lifecycle_label || '已归档' }}</el-tag>
                  <el-tag
                    v-else-if="row.platform === 'xiaohongshu' && row.has_history && row.lifecycle_status === 'expired'"
                    size="small"
                    type="warning"
                    effect="plain"
                  >有历史</el-tag>
                  <template v-if="row.platform === 'xiaohongshu' && row.xhs_sessions && !row.lifecycle_readonly">
                    <el-tag size="small" :type="xhsSessionType(row.xhs_sessions.pc, row.xhs_sessions.pc_valid)" effect="plain">
                      {{ xhsSessionLabel('PC', row.xhs_sessions.pc, row.xhs_sessions.pc_valid) }}
                    </el-tag>
                    <el-tag size="small" :type="xhsSessionType(row.xhs_sessions.creator, row.xhs_sessions.creator_valid)" effect="plain">
                      {{ xhsSessionLabel('创作者', row.xhs_sessions.creator, row.xhs_sessions.creator_valid) }}
                    </el-tag>
                  </template>
                  <el-tag v-else-if="!row.lifecycle_readonly" size="small" :type="loginStatusType(row)" effect="plain">
                    {{ loginStatusLabel(row) }}
                  </el-tag>
                  <el-tag
                    v-if="!row.proxy_assigned && !row.proxy_ip_slot && !row.proxy_url"
                    size="small"
                    type="warning"
                    effect="plain"
                  >无代理</el-tag>
                </div>

                <div class="acc-card-phone" v-if="boundInfo(row)">
                  <el-tag size="small" effect="plain" :type="boundInfo(row).relogin_mode === 'auto' ? 'success' : 'info'">
                    📱 {{ boundInfo(row).phone_mask }}
                  </el-tag>
                  <span class="mode-pill" :class="boundInfo(row).relogin_mode === 'auto' ? 'auto' : 'manual'"
                        @click="toggleBindingMode(row)" title="点击切换自动/手动登录">
                    {{ boundInfo(row).relogin_mode === 'auto' ? '失效自动登录' : '手动登录' }}
                  </span>
                  <el-button size="small" text type="primary" @click="openBindDialog(row)">绑定</el-button>
                </div>
                <div class="acc-card-phone" v-else-if="row.platform !== 'xiaohongshu'">
                  <el-button size="small" text type="info" @click="openBindDialog(row)">
                    + 绑定手机号
                  </el-button>
                </div>

                <div class="acc-card-stats">
                  <div class="stat">
                    <span class="stat-k">粉丝</span>
                    <span class="stat-v">{{ formatStatNum(row.stats?.followers) }}</span>
                  </div>
                  <div class="stat">
                    <span class="stat-k">阅读</span>
                    <span class="stat-v">{{ formatStatNum(row.stats?.reads) }}</span>
                  </div>
                  <div class="stat">
                    <span class="stat-k">今日</span>
                    <span class="stat-v">{{ row.publishes_today || 0 }}/{{ row.daily_quota || 0 }}</span>
                  </div>
                  <div class="stat">
                    <span class="stat-k">代理</span>
                    <span class="stat-v">{{ row.proxy_ip_slot || (row.proxy_url ? '已设' : '—') }}</span>
                  </div>
                </div>

                <div class="acc-card-actions">
                  <el-button size="small" type="primary" @click="openLogin(row)">登录</el-button>
                  <el-button
                    v-if="row.platform === 'xiaohongshu'"
                    size="small"
                    type="warning"
                    plain
                    @click="openXhsBoard(row)"
                  >{{ row.lifecycle_readonly ? '历史看板' : '看板' }}</el-button>
                  <el-button
                    v-if="row.platform === 'zhihu'"
                    size="small"
                    type="warning"
                    plain
                    @click="openZhihuBoard(row)"
                  >看板</el-button>
                  <el-button
                    v-if="row.platform === 'douyin'"
                    size="small"
                    type="warning"
                    plain
                    @click="openDouyinBoard(row)"
                  >看板</el-button>
                  <el-button
                    v-if="row.platform === 'toutiao'"
                    size="small"
                    type="warning"
                    plain
                    @click="openToutiaoBoard(row)"
                  >看板</el-button>
                  <el-button
                    v-if="row.platform === 'baijiahao'"
                    size="small"
                    type="warning"
                    plain
                    @click="openBaijiahaoBoard(row)"
                  >看板</el-button>
                  <el-button
                    size="small"
                    :disabled="!row.homepage_url || row.lifecycle_readonly"
                    @click="openHomepage(row)"
                  >主页</el-button>
                  <el-button size="small" :disabled="row.lifecycle_readonly" @click="editProxy(row)">代理</el-button>
                  <el-button size="small" :disabled="row.lifecycle_readonly" @click="refreshProfile(row)">同步</el-button>
                  <el-button
                    size="small"
                    type="danger"
                    text
                    @click="deleteAccount(row)"
                  >{{ row.platform === 'xiaohongshu' && !row.lifecycle_readonly ? '归档' : '删除' }}</el-button>
                </div>
              </article>
            </div>
          </div>
        </div>
      </el-tab-pane>

      <!-- ========== 队列 ========== -->
      <el-tab-pane name="jobs">
        <template #label>
          发布队列
          <span v-if="(stats.pending || 0) + (stats.running || 0)" class="tab-count warn">
            {{ (stats.pending || 0) + (stats.running || 0) }}
          </span>
        </template>

        <div v-if="activeCampaigns.length" class="campaign-strip">
          <div class="campaign-strip-header">
            <span class="section-label">活跃批次</span>
            <el-button size="small" text @click="loadCampaigns">刷新</el-button>
          </div>
          <div class="campaign-cards">
            <div
              v-for="c in activeCampaigns"
              :key="c.campaign_id"
              class="campaign-card"
              :class="{ active: campaignFilter === c.campaign_id }"
            >
              <div class="campaign-card-head">
                <span class="mono">#{{ c.campaign_id.slice(0, 6) }}</span>
                <el-tag size="small" :type="c.active > 0 ? 'primary' : 'success'" effect="plain">
                  {{ c.active > 0 ? '进行中' : '已完成' }}
                </el-tag>
                <span class="muted">{{ c.article_count }}篇 · {{ c.platform_count }}平台</span>
              </div>
              <el-progress :percentage="c.progress" :stroke-width="6" :show-text="false" />
              <div class="campaign-stats">
                <span>共 {{ c.total }}</span>
                <span class="ok">✓ {{ c.success }}</span>
                <span v-if="c.draft" class="warn">◐ {{ c.draft }}</span>
                <span v-if="c.failed" class="bad">✗ {{ c.failed }}</span>
                <span v-if="c.active" class="info">… {{ c.active }}</span>
              </div>
              <div class="campaign-actions">
                <el-button
                  size="small"
                  :type="campaignFilter === c.campaign_id ? 'primary' : 'default'"
                  @click="toggleCampaignFilter(c.campaign_id)"
                >
                  {{ campaignFilter === c.campaign_id ? '取消过滤' : '仅看本批' }}
                </el-button>
                <el-button v-if="c.pending > 0" size="small" type="danger" text @click="cancelCampaign(c)">
                  取消剩余
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="list-toolbar jobs-toolbar">
          <el-select v-model="jobFilter" placeholder="全部状态" clearable style="width: 140px" @change="loadJobs">
            <el-option label="待发布" value="pending" />
            <el-option label="执行中" value="running" />
            <el-option label="正式发布" value="success" />
            <el-option label="仅草稿" value="draft" />
            <el-option label="失败" value="failed" />
            <el-option label="已取消" value="cancelled" />
          </el-select>
          <el-tag v-if="campaignFilter" closable type="primary" @close="clearCampaignFilter">
            批次 #{{ campaignFilter.slice(0, 6) }}
          </el-tag>
          <el-button @click="loadJobs">刷新</el-button>
          <span class="poll-hint">{{ pollHint }}</span>
        </div>

        <div class="mp-panel job-panel">
          <el-table :data="jobs" v-loading="loadingJobs" class="clean-table">
            <el-table-column label="内容" min-width="160">
              <template #default="{ row }">
                <div class="job-title">文章 #{{ row.article_id }}</div>
                <div class="job-sub mono">Job #{{ row.id }}</div>
              </template>
            </el-table-column>
            <el-table-column label="目标" min-width="200">
              <template #default="{ row }">
                <div class="acc-inline">
                  <el-avatar :size="28" :src="avatarSrc(row)">{{ (row.display_name || '?')[0] }}</el-avatar>
                  <div>
                    <div class="acc-name sm">{{ row.display_name || row.account_id }}</div>
                    <div class="job-sub">{{ platformName(row.platform) }}</div>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template #default="{ row }">
                <el-tag :type="jobStatusType(row.status)" size="small" effect="plain">
                  {{ jobStatusLabel(row.status) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="scheduled_at" label="计划时间" width="160" />
            <el-table-column label="说明" min-width="160" show-overflow-tooltip>
              <template #default="{ row }">
                <span v-if="row.error_message" :class="{ warn: row.status === 'pending' }">{{ row.error_message }}</span>
                <span v-else class="muted">—</span>
              </template>
            </el-table-column>
            <el-table-column label="链接" width="96">
              <template #default="{ row }">
                <el-link
                  v-if="row.status === 'success' && row.post_url && !row.post_url.includes('/edit')"
                  :href="row.post_url"
                  target="_blank"
                  type="primary"
                >查看</el-link>
                <el-link
                  v-else-if="row.status === 'draft' && row.post_url"
                  :href="row.post_url"
                  target="_blank"
                  type="warning"
                >草稿</el-link>
              </template>
            </el-table-column>
            <el-table-column label="" width="72" fixed="right">
              <template #default="{ row }">
                <el-button
                  v-if="row.status === 'pending' || row.status === 'running'"
                  size="small"
                  type="danger"
                  text
                  @click="cancelJob(row)"
                >取消</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <el-tab-pane name="sms" label="短信">
        <SmsGateway embedded only="messages" />
      </el-tab-pane>
    </el-tabs>

    <el-dialog v-model="showBatchCreate" title="批量创建矩阵号" width="480px">
      <el-form :model="batchForm" label-width="88px">
        <el-form-item label="平台">
          <el-select v-model="batchForm.platform" style="width: 100%">
            <el-option v-for="p in batchCreatePlatforms" :key="p.code" :label="p.name" :value="p.code" />
          </el-select>
        </el-form-item>
        <el-form-item label="数量">
          <el-input-number v-model="batchForm.count" :min="1" :max="200" style="width: 100%" />
          <p class="hint">从 {{ batchCreatePreview.first }} 起编号，关闭登录窗口将停止后续添加</p>
        </el-form-item>
        <el-form-item label="ID前缀">
          <el-input v-model="batchForm.prefix" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showBatchCreate = false">取消</el-button>
        <el-button type="primary" :loading="batchCreating" @click="batchCreateAccounts">开始添加</el-button>
      </template>
    </el-dialog>

    <AccountLoginDialog
      v-model="loginDialogVisible"
      :platform="loginTarget.platform"
      :account-id="loginTarget.account_id"
      :platform-label="platformName(loginTarget.platform)"
      :xhs-sessions="loginTarget.xhs_sessions"
      :interactive-login="loginCapability.interactive_login"
      :vnc-web-viewer="loginCapability.vnc_web_viewer"
      :vnc-viewer-path="loginCapability.vnc_viewer_path || DEFAULT_VNC_VIEWER_PATH"
      :vnc-ws-path="loginCapability.vnc_ws_path || DEFAULT_VNC_WS_PATH"
      :batch-current="batchProgress.active ? batchProgress.current : 0"
      :batch-total="batchProgress.active ? batchProgress.total : 0"
      @success="onLoginSuccess"
    />

    <el-dialog v-model="bindDialog" :title="`绑定手机号 - ${platformName(bindForm.platform)}/${bindForm.account_id}`" width="520px">
      <el-form label-width="100px" @submit.prevent>
        <el-form-item label="手机号">
          <el-input v-model="bindForm.phone" maxlength="11" placeholder="请输入 11 位手机号" />
        </el-form-item>
        <el-form-item label="登录模式">
          <el-radio-group v-model="bindForm.relogin_mode">
            <el-radio value="auto">失效自动登录（自动发码+自动读码）</el-radio>
            <el-radio value="manual">手动登录（仅提醒）</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item>
          <div class="bind-hint">同一平台内手机号唯一，不同平台可共用同一手机号。</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="bindDialog = false">取消</el-button>
        <el-button type="primary" :loading="bindSaving" @click="saveBinding">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import AccountLoginDialog from '../components/AccountLoginDialog.vue'
import SmsGateway from './SmsGateway.vue'
import SectionHeader from '../components/shell/SectionHeader.vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import {
  DEFAULT_VNC_VIEWER_PATH,
  DEFAULT_VNC_WS_PATH,
  PUBLISH_V2,
  publishV2PublicUrl,
  resolveApiBaseURL,
} from '../utils/appBase'

const API_BASE = resolveApiBaseURL()
const V2 = PUBLISH_V2

const avatarSrc = (row) => {
  const u = row?.avatar_url
  if (!u) return ''
  if (u.startsWith('http://') || u.startsWith('https://')) {
    if (/xiaohongshu|xhscdn/i.test(u)) {
      return publishV2PublicUrl(`/accounts/${row.platform}/${row.account_id}/avatar`)
    }
    return u
  }
  if (u.startsWith('/api/')) return `${API_BASE}${u}`
  return u
}

const tab = ref('accounts')
const accounts = ref([])
const jobs = ref([])
const platforms = ref([])
const stats = ref({})
const batchStats = ref({})
const loadingAccounts = ref(false)
const loadingJobs = ref(false)
const jobFilter = ref('')
const campaignFilter = ref('')
const campaigns = ref([])
const platformFilter = ref('')
const accountQuery = ref('')
const healthFilter = ref('all')
const nativeApiPlatforms = ref(['zhihu', 'toutiao', 'baijiahao', 'bilibili', 'weibo'])
const refreshingAccounts = ref(false)
const autoLoginAllLoading = ref(false)
const bindings = ref([])
const bindingsMap = ref({})
const bindDialog = ref(false)
const bindForm = ref({ platform: '', account_id: '', phone: '', relogin_mode: 'auto' })
const bindSaving = ref(false)
const monitorCandidates = ref([])
let profileRefreshAbort = null
const showBatchCreate = ref(false)
const batchCreating = ref(false)
const batchProgress = ref({ active: false, current: 0, total: 0, accountId: '' })
let batchLoginResolve = null
let batchLoginSucceeded = false

const BATCH_CREATE_PLATFORMS = [
  'zhihu', 'toutiao', 'baijiahao', 'xiaohongshu', 'douyin',
  'bilibili', 'kuaishou', 'weibo',
]
const PRIORITY_PLATFORMS = [
  'toutiao', 'baijiahao', 'bilibili', 'zhihu', 'xiaohongshu', 'douyin',
  'kuaishou', 'weibo',
]

const batchCreatePlatforms = computed(() =>
  BATCH_CREATE_PLATFORMS.map((code) => ({ code, name: platformName(code) })),
)

const batchForm = ref({
  platform: 'zhihu', count: 5, prefix: 'matrix',
  group_name: '', min_interval_sec: 300, daily_quota: 20,
})

const batchCreatePreview = computed(() => {
  const plat = batchForm.value.platform
  const prefix = batchForm.value.prefix || 'matrix'
  const count = batchForm.value.count || 1
  const samePlatform = accounts.value.filter((a) => a.platform === plat)
  const re = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}_(\\d+)$`)
  let maxNum = 0
  for (const a of samePlatform) {
    const m = re.exec(a.account_id || '')
    if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10))
  }
  const start = maxNum + 1
  const end = start + count - 1
  const first = `${prefix}_${String(start).padStart(3, '0')}`
  const last = `${prefix}_${String(end).padStart(3, '0')}`
  return { start, first, last, existing: samePlatform.length }
})

const accountsByPlatform = computed(() => {
  const map = {}
  for (const a of accounts.value) {
    if (!map[a.platform]) map[a.platform] = []
    map[a.platform].push(a)
  }
  const codes = [...new Set([...PRIORITY_PLATFORMS, ...Object.keys(map)])]
  return codes
    .filter((code) => (map[code] || []).length)
    .map((code) => ({
      code,
      name: platformName(code),
      accounts: map[code],
      isApiNative: nativeApiPlatforms.value.includes(code),
    }))
})

const isLoginBad = (row) =>
  row.health_status === 'invalid' || row.login_status === 'expired' || row.login_status === 'partial'
    || (!row.has_cookie && row.login_status !== 'logged_in')

const isNoProxy = (row) => !row.proxy_assigned && !row.proxy_ip_slot && !row.proxy_url

const filteredAccounts = computed(() => {
  let list = accounts.value
  if (platformFilter.value) list = list.filter((a) => a.platform === platformFilter.value)
  const q = accountQuery.value.trim().toLowerCase()
  if (q) {
    list = list.filter((a) =>
      (a.display_name || '').toLowerCase().includes(q)
      || (a.account_id || '').toLowerCase().includes(q)
      || (a.legacy_matrix_id || '').toLowerCase().includes(q)
      || (a.external_user_id || '').toLowerCase().includes(q),
    )
  }
  if (healthFilter.value === 'bad') list = list.filter(isLoginBad)
  else if (healthFilter.value === 'nologin') {
    list = list.filter((a) => a.login_status !== 'logged_in' && !a.has_cookie)
  } else if (healthFilter.value === 'noproxy') list = list.filter(isNoProxy)
  else if (healthFilter.value === 'ok') {
    list = list.filter((a) => !isLoginBad(a) && a.login_status === 'logged_in' && !a.lifecycle_readonly)
  } else if (healthFilter.value === 'archived') {
    list = list.filter((a) => a.platform === 'xiaohongshu' && a.lifecycle_readonly)
  }
  return list
})

const statusChips = computed(() => {
  const all = accounts.value
  return [
    { key: 'all', label: '全部', count: all.length },
    { key: 'ok', label: '正常', count: all.filter((a) => a.login_status === 'logged_in' && !isLoginBad(a) && !a.lifecycle_readonly).length },
    { key: 'bad', label: '异常', count: all.filter(isLoginBad).length },
    { key: 'nologin', label: '未登录', count: all.filter((a) => a.login_status !== 'logged_in' && !a.has_cookie && !a.lifecycle_readonly).length },
    { key: 'noproxy', label: '无代理', count: all.filter(isNoProxy).length },
    {
      key: 'archived',
      label: '注销/归档',
      count: all.filter((a) => a.platform === 'xiaohongshu' && a.lifecycle_readonly).length,
    },
  ]
})

const loggedInCount = computed(() =>
  accounts.value.filter((a) => a.login_status === 'logged_in' || a.has_cookie).length,
)

const successRate = computed(() => {
  const s = stats.value.success || 0
  const f = stats.value.failed || 0
  const d = stats.value.draft || 0
  const done = s + f + d
  if (!done) return batchStats.value.success_rate || 0
  return Math.round((s / done) * 100)
})

const kpiItems = computed(() => [
  { key: 'pending', label: '待发布', value: stats.value.pending || 0, filter: 'pending', tone: 'warn' },
  { key: 'running', label: '执行中', value: stats.value.running || 0, filter: 'running', tone: 'info' },
  { key: 'success', label: '已发布', value: stats.value.success || 0, filter: 'success', tone: 'ok' },
  { key: 'draft', label: '草稿', value: stats.value.draft || 0, filter: 'draft', tone: 'info' },
  { key: 'failed', label: '失败', value: stats.value.failed || 0, filter: 'failed', tone: 'bad' },
])

const focusJobs = (filter) => {
  tab.value = 'jobs'
  jobFilter.value = filter || ''
  loadJobs()
}

const platformName = (code) => {
  const names = {
    xiaohongshu: '小红书', douyin: '抖音', toutiao: '头条', baijiahao: '百家号',
    bilibili: 'B站', zhihu: '知乎', kuaishou: '快手', weibo: '微博', weixin: '微信',
    tencent_channels: '视频号', csdn: 'CSDN', juejin: '掘金',
  }
  return names[code] || code
}

const jobStatusType = (s) => ({ pending: 'info', running: 'warning', success: 'success', draft: 'warning', failed: 'danger', cancelled: 'info' }[s] || '')
const jobStatusLabel = (s) => ({ pending: '待发布', running: '执行中', success: '正式发布', draft: '仅草稿', failed: '失败', cancelled: '已取消' }[s] || s)

const loadPlatforms = async () => {
  const res = await axios.get(`${V2}/platforms`)
  platforms.value = res.data.platforms || []
}

const loadAccounts = async () => {
  loadingAccounts.value = true
  try {
    const res = await axios.get(`${V2}/accounts`)
    accounts.value = res.data.items || []
    if (res.data.native_api_platforms?.length) {
      nativeApiPlatforms.value = res.data.native_api_platforms
    }
    await loadBindings()
  } finally { loadingAccounts.value = false }
}

const loadBindings = async () => {
  try {
    const res = await axios.get(`${V2}/phone-bindings`)
    bindings.value = res.data.items || []
    const map = {}
    for (const b of bindings.value) {
      map[`${b.platform}__${b.account_id}`] = b
    }
    bindingsMap.value = map
  } catch (e) {
    // 绑定接口尚未就绪时静默忽略
  }
}

const boundInfo = (row) => bindingsMap.value[`${row.platform}__${row.account_id}`] || null

const openBindDialog = (row) => {
  bindForm.value = {
    platform: row.platform,
    account_id: row.account_id,
    phone: boundInfo(row)?.phone || '',
    relogin_mode: boundInfo(row)?.relogin_mode || 'auto',
  }
  bindDialog.value = true
}

const saveBinding = async () => {
  bindSaving.value = true
  try {
    const cur = bindingsMap.value[`${bindForm.value.platform}__${bindForm.value.account_id}`]
    if (cur) {
      // 已绑定：更新手机号（需先解绑旧的再绑新的，避免手机号唯一冲突）
      if (cur.phone !== bindForm.value.phone) {
        await axios.delete(`${V2}/phone-bindings/${cur.id}`)
      }
      if (cur.phone !== bindForm.value.phone) {
        await axios.post(`${V2}/phone-bindings`, bindForm.value)
      } else {
        await axios.put(`${V2}/phone-bindings/${cur.id}/mode`, { relogin_mode: bindForm.value.relogin_mode })
      }
    } else {
      await axios.post(`${V2}/phone-bindings`, bindForm.value)
    }
    ElMessage.success('手机号绑定已保存')
    bindDialog.value = false
    await loadAccounts()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '绑定失败')
  } finally { bindSaving.value = false }
}

const removeBinding = async (row) => {
  const b = boundInfo(row)
  if (!b) return
  await ElMessageBox.confirm(`确认解绑 ${platformName(row.platform)}/${row.account_id} 的手机号 ${b.phone_mask}？`, '解绑确认', { type: 'warning' })
  try {
    await axios.delete(`${V2}/phone-bindings/${b.id}`)
    ElMessage.success('已解绑')
    await loadAccounts()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '解绑失败')
  }
}

const toggleBindingMode = async (row) => {
  const b = boundInfo(row)
  if (!b) return
  const next = b.relogin_mode === 'auto' ? 'manual' : 'auto'
  try {
    await axios.put(`${V2}/phone-bindings/${b.id}/mode`, { relogin_mode: next })
    b.relogin_mode = next
    ElMessage.success(next === 'auto' ? '已切换为失效自动登录' : '已切换为手动登录')
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '切换失败')
  }
}

const loadMonitorCandidates = async () => {
  try {
    const res = await axios.get(`${V2}/phone-monitor-summary`)
    monitorCandidates.value = res.data.items || []
  } catch (e) { monitorCandidates.value = [] }
}

const loginStatusLabel = (row) => {
  if (row.health_status === 'invalid' || row.login_status === 'expired') return '登录失效'
  if (row.login_status === 'partial') return '部分登录'
  if (row.login_status === 'logged_in') return '已登录'
  if (row.has_cookie) return '有 Cookie'
  return '未登录'
}

const loginStatusType = (row) => {
  if (row.health_status === 'invalid' || row.login_status === 'expired') return 'danger'
  if (row.login_status === 'partial') return 'warning'
  if (row.login_status === 'logged_in' || row.health_status === 'ok') return 'success'
  return 'info'
}

const xhsSessionType = (has, valid) => {
  if (!has) return 'info'
  if (valid === false) return 'danger'
  return 'success'
}

const xhsSessionLabel = (name, has, valid) => {
  if (!has) return `${name}×`
  if (valid === false) return `${name}失效`
  return `${name}✓`
}

const formatStatNum = (n) => {
  if (n == null || n === '') return '—'
  const v = Number(n)
  if (Number.isNaN(v)) return String(n)
  if (v >= 10000) return `${(v / 10000).toFixed(1)}万`
  return String(v)
}

const refreshProfile = async (row) => {
  try {
    const res = await axios.post(`${V2}/accounts/${row.platform}/${row.account_id}/refresh-profile`)
    if (res.data.merged) {
      ElMessage.success(res.data.message || '检测到重复账号，已自动合并')
    } else if (res.data.ok) {
      ElMessage.success('资料已更新')
    } else {
      ElMessage.warning(res.data.profile?.error || '登录可能已过期')
    }
    loadAccounts()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '刷新失败')
  }
}

const openHomepage = (row) => {
  const url = (row.homepage_url || '').trim()
  if (!url) {
    ElMessage.warning('暂无主页链接，请先同步账号资料')
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

const refreshAllProfiles = async (silent = false) => {
  profileRefreshAbort?.abort()
  profileRefreshAbort = new AbortController()
  try {
    const res = await axios.post(`${V2}/accounts/refresh-profiles`, null, {
      signal: profileRefreshAbort.signal,
    })
    if (!silent) ElMessage.success(`已同步 ${res.data.total} 个账号`)
    await loadAccounts()
    return res.data
  } catch (e) {
    if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return null
    if (!silent) ElMessage.error(e.response?.data?.detail || '同步失败')
    throw e
  } finally {
    profileRefreshAbort = null
  }
}


const syncYimeiCookies = async () => {
  try {
    await ElMessageBox.confirm(
      '将从本机易媒助手拉取 Cookie：用 Cookie 向各平台核实稳定 ID 后新建或覆盖；不会删除库内其它账号（含已注销）。是否继续？',
      '同步易媒',
      { type: 'info', confirmButtonText: '开始同步', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  yimeiPullLoading.value = true
  try {
    const d = await publishV2Api.yimeiPull({ clear_other_cookies: false })
    const imported = d?.imported ?? 0
    const failed = d?.failed ?? 0
    const created = d?.created ?? 0
    const merged = d?.merged ?? 0
    if (failed) {
      ElMessage.warning(`同步完成：成功 ${imported}（新建 ${created} / 覆盖 ${merged}），失败 ${failed}`)
    } else {
      ElMessage.success(`已同步易媒 ${imported} 个（新建 ${created} / 覆盖 ${merged}）`)
    }
    await loadAccounts()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || e.message || '同步易媒失败')
  } finally {
    yimeiPullLoading.value = false
  }
}

const refreshAccounts = async () => {
  refreshingAccounts.value = true
  try {
    await loadAccounts()
    await refreshAllProfiles(true)
    ElMessage.success('账号列表与资料已刷新')
  } catch {
    /* already messaged */
  } finally {
    refreshingAccounts.value = false
  }
}

const autoLoginAll = async () => {
  try {
    await ElMessageBox.confirm(
      '将对所有已绑定手机号的百家号/抖音/头条账号自动登录（自动发验证码并自动读取短信上号），知乎与未绑定手机号的账号将跳过。确认继续？',
      '一键全登录',
      { type: 'warning', confirmButtonText: '开始全登录', cancelButtonText: '取消' },
    )
  } catch {
    return
  }
  autoLoginAllLoading.value = true
  try {
    const res = await axios.post(`${V2}/cookies/platform-api/auto-login-all`)
    const results = res.data.results || []
    const ok = results.filter((r) => r.ok)
    const skipped = results.filter((r) => !r.ok)
    if (ok.length) {
      ElMessage.success(`已发起 ${ok.length} 个账号自动登录，系统将自动读取短信验证码上号`)
    }
    if (skipped.length) {
      const names = skipped.map((r) => `${r.platform}/${r.account_id}`).join('、')
      ElMessage.warning(`${skipped.length} 个跳过：${names}`)
    }
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || '一键全登录失败')
  } finally {
    autoLoginAllLoading.value = false
  }
}

const loadJobs = async () => {
  loadingJobs.value = true
  try {
    const res = await axios.get(`${V2}/jobs`, {
      params: {
        status: jobFilter.value || undefined,
        campaign_id: campaignFilter.value || undefined,
        limit: 200,
      },
    })
    jobs.value = res.data.items || []
    stats.value = res.data.stats || {}
  } finally { loadingJobs.value = false }
}

const loadCampaigns = async () => {
  try {
    const res = await axios.get(`${V2}/campaigns`, { params: { limit: 10 } })
    campaigns.value = res.data.items || []
  } catch { /* ignore */ }
}

const activeCampaigns = computed(() => {
  const now = Date.now()
  return (campaigns.value || []).filter((c) => {
    if (c.active > 0) return true
    if (c.last_finished_at) {
      const t = new Date(c.last_finished_at).getTime()
      if (now - t < 2 * 3600 * 1000) return true
    }
    return false
  })
})

const hasRunningWork = computed(() => {
  if (activeCampaigns.value.some((c) => c.active > 0)) return true
  return (stats.value.pending || 0) + (stats.value.running || 0) > 0
})

const pollHint = computed(() => `自动刷新 ${hasRunningWork.value ? 3 : 30}s`)

const toggleCampaignFilter = (cid) => {
  campaignFilter.value = campaignFilter.value === cid ? '' : cid
  loadJobs()
}

const clearCampaignFilter = () => {
  campaignFilter.value = ''
  loadJobs()
}

const cancelCampaign = async (c) => {
  try {
    await ElMessageBox.confirm(
      `确认取消批次 #${c.campaign_id.slice(0, 6)} 剩余 ${c.pending} 个待发布任务？`,
      '取消批次剩余任务',
      { type: 'warning', confirmButtonText: '取消剩余', cancelButtonText: '保留' },
    )
  } catch { return }
  try {
    const res = await axios.post(`${V2}/campaigns/${c.campaign_id}/cancel-pending`)
    ElMessage.success(`已取消 ${res.data.cancelled} 个任务`)
    loadCampaigns()
    loadJobs()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || e.message)
  }
}

const cancelJob = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定取消任务 #${row.id}？`,
      '取消发布',
      { type: 'warning', confirmButtonText: '取消任务', cancelButtonText: '保留' },
    )
  } catch { return }
  try {
    await axios.post(`${V2}/jobs/${row.id}/cancel`)
    ElMessage.success('任务已取消')
    loadJobs()
  } catch (e) {
    ElMessage.error(e.response?.data?.detail || e.message)
  }
}

const loadBatchStats = async () => {
  try {
    const res = await axios.get(`${V2}/stats`)
    batchStats.value = res.data || {}
  } catch { /* ignore */ }
}

const toggleAccount = async (row) => {
  try {
    await axios.patch(`${V2}/accounts/${row.platform}/${row.account_id}`, null, {
      params: { enabled: row.enabled },
    })
  } catch (e) {
    row.enabled = !row.enabled
    ElMessage.error('切换失败')
  }
}

const editProxy = async (row) => {
  try {
    const { value } = await ElMessageBox.prompt(
      '登录、发布、拉数据均走此代理。\n格式：http://user:pass@host:port\n留空清除。',
      `${row.account_id} 代理设置`,
      {
        confirmButtonText: '保存',
        cancelButtonText: '取消',
        inputValue: row.proxy_url || '',
        inputPlaceholder: 'http://user:pass@host:port',
      },
    )
    await axios.patch(`${V2}/accounts/${row.platform}/${row.account_id}`, null, {
      params: { proxy_url: (value || '').trim() },
    })
    ElMessage.success('代理已保存')
    await loadAccounts()
  } catch (e) {
    if (e === 'cancel' || e?.message === 'cancel') return
    ElMessage.error(e.response?.data?.detail || e.message || '保存失败')
  }
}

const deleteAccount = async (row) => {
  const isXhs = row.platform === 'xiaohongshu'
  const alreadyArchived = Boolean(row.lifecycle_readonly)
  try {
    if (isXhs && !alreadyArchived) {
      await ElMessageBox.confirm(
        `将归档「${row.display_name || row.account_id}」。\n保留小红书用户 ID（${row.external_user_id || '未绑定'}）对应的历史看板数据；不再用于发布。\n\n如需彻底删除请在确认后联系管理员走硬删。`,
        '归档小红书账号',
        { type: 'warning', confirmButtonText: '归档保留历史' },
      )
      profileRefreshAbort?.abort()
      refreshingAccounts.value = false
      await axios.delete(`${V2}/accounts/${row.platform}/${row.account_id}`, {
        params: { delete_cookie: true, hard: false },
      })
      ElMessage.success('已归档，历史看板仍可查看')
    } else {
      await ElMessageBox.confirm(
        isXhs
          ? `确认彻底删除 ${row.account_id}？将移除矩阵记录（历史快照若已按用户 ID 入库仍保留在库中）。`
          : `确认删除 ${platformName(row.platform)} / ${row.account_id}？`,
        '删除确认',
        { type: 'warning' },
      )
      profileRefreshAbort?.abort()
      refreshingAccounts.value = false
      await axios.delete(`${V2}/accounts/${row.platform}/${row.account_id}`, {
        params: { delete_cookie: true, hard: true },
      })
      ElMessage.success('已删除')
    }
    loadAccounts()
  } catch (e) {
    if (e === 'cancel' || e?.message === 'cancel') return
    ElMessage.error(e.response?.data?.detail || e.message || '操作失败')
  }
}

const buildCreatedAccountList = (data) => {
  const plat = batchForm.value.platform
  if (data.accounts?.length === data.created) {
    return data.accounts.map((a) => ({ platform: a.platform, account_id: a.account_id }))
  }
  const { start_id: start, end_id: end } = data
  if (!start || !end) return []
  const re = /^(.+)_(\d+)$/
  const ms = re.exec(start)
  const me = re.exec(end)
  if (!ms || !me || ms[1] !== me[1]) return []
  const prefix = ms[1]
  const pad = ms[2].length
  const from = parseInt(ms[2], 10)
  const to = parseInt(me[2], 10)
  const list = []
  for (let i = from; i <= to; i++) {
    list.push({ platform: plat, account_id: `${prefix}_${String(i).padStart(pad, '0')}` })
  }
  return list
}

const deleteAccountSilent = async ({ platform, account_id }) => {
  try {
    await axios.delete(`${V2}/accounts/${platform}/${account_id}`, { params: { delete_cookie: true } })
  } catch { /* ignore */ }
}

const promptBatchLogin = async (account) => {
  await loadLoginCapability()
  return new Promise((resolve) => {
    batchLoginResolve = resolve
    batchLoginSucceeded = false
    batchProgress.value.accountId = account.account_id
    loginTarget.value = {
      platform: account.platform,
      account_id: account.account_id,
      xhs_sessions: null,
    }
    loginDialogVisible.value = true
  })
}

const runBatchCreateAndLogin = async () => {
  const total = batchForm.value.count
  const formBase = { ...batchForm.value }
  batchProgress.value = { active: true, current: 0, total, accountId: '' }
  showBatchCreate.value = false

  let successCount = 0
  for (let i = 0; i < total; i++) {
    batchProgress.value.current = i + 1
    let account
    try {
      const res = await axios.post(`${V2}/accounts/batch`, { ...formBase, count: 1 })
      account = buildCreatedAccountList(res.data)[0]
      if (!account) throw new Error('empty')
    } catch (e) {
      ElMessage.error(e.response?.data?.detail || '创建账号失败，已停止')
      break
    }

    const ok = await promptBatchLogin(account)
    if (!ok) {
      await deleteAccountSilent(account)
      ElMessage.warning(`已取消 ${account.account_id}，后续账号不再创建`)
      break
    }
    successCount++
    await loadAccounts()
  }

  batchProgress.value = { active: false, current: 0, total: 0, accountId: '' }
  if (successCount > 0) ElMessage.success(`批量添加完成：成功 ${successCount} 个`)
  else if (total > 0) ElMessage.info('未添加任何账号')
}

const batchCreateAccounts = async () => {
  batchCreating.value = true
  try {
    await runBatchCreateAndLogin()
  } finally { batchCreating.value = false }
}

const route = useRoute()
const router = useRouter()

const openXhsBoard = (row) => {
  const key = String(row?.external_user_id || row?.account_id || '').trim()
  if (!key) return
  router.push(`/data-analytics/xhs/${encodeURIComponent(key)}`)
}

const openZhihuBoard = (row) => {
  const key = String(row?.external_user_id || row?.account_id || '').trim()
  if (!key) return
  router.push(`/data-analytics/zhihu/${encodeURIComponent(key)}`)
}

const openDouyinBoard = (row) => {
  const key = String(row?.external_user_id || row?.account_id || '').trim()
  if (!key) return
  router.push(`/data-analytics/douyin/${encodeURIComponent(key)}`)
}

const openToutiaoBoard = (row) => {
  const key = String(row?.external_user_id || row?.account_id || '').trim()
  if (!key) return
  router.push(`/data-analytics/toutiao/${encodeURIComponent(key)}`)
}

const openBaijiahaoBoard = (row) => {
  const key = String(row?.external_user_id || row?.account_id || '').trim()
  if (!key) return
  router.push(`/data-analytics/baijiahao/${encodeURIComponent(key)}`)
}

const loginCapability = ref({
  interactive_login: true,
  vnc_web_viewer: false,
  vnc_viewer_path: DEFAULT_VNC_VIEWER_PATH,
  vnc_ws_path: DEFAULT_VNC_WS_PATH,
  message: '',
})
const loginDialogVisible = ref(false)
const loginTarget = ref({ platform: '', account_id: '', xhs_sessions: null })

const onLoginSuccess = async () => {
  batchLoginSucceeded = true
  await loadAccounts()
}

const openLogin = async (row) => {
  batchLoginResolve = null
  await loadLoginCapability()
  loginTarget.value = {
    platform: row.platform,
    account_id: row.account_id,
    xhs_sessions: row.xhs_sessions || null,
  }
  loginDialogVisible.value = true
}

const loadLoginCapability = async () => {
  try {
    const res = await axios.get(`${V2}/cookies/login-capability`)
    loginCapability.value = res.data
  } catch { loginCapability.value = { interactive_login: false, message: '' } }
}

watch(loginDialogVisible, (visible, wasVisible) => {
  if (wasVisible && !visible && batchLoginResolve) {
    const resolve = batchLoginResolve
    batchLoginResolve = null
    resolve(batchLoginSucceeded)
    batchLoginSucceeded = false
  }
})

let pollTimer = null
let lastPollInterval = 0

const schedulePoll = () => {
  const desired = hasRunningWork.value ? 3000 : 30000
  if (desired === lastPollInterval && pollTimer) return
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
  lastPollInterval = desired
  pollTimer = setInterval(() => {
    if (tab.value !== 'jobs') return
    loadJobs()
    loadBatchStats()
    loadCampaigns()
    schedulePoll()
  }, desired)
}

onMounted(async () => {
  if (route.query.tab === 'jobs' || route.query.tab === 'queue') tab.value = 'jobs'
  if (route.query.filter) {
    tab.value = 'jobs'
    jobFilter.value = String(route.query.filter)
  }
  if (route.query.tab === 'sms') tab.value = 'sms'
  if (route.query.campaign_id) {
    campaignFilter.value = String(route.query.campaign_id)
    tab.value = 'jobs'
  }
  if (route.query.tab === 'published') {
    router.push('/data-analytics')
    return
  }
  if (route.query.health === 'bad' || route.query.health === 'expired') {
    tab.value = 'accounts'
    healthFilter.value = 'bad'
  }
  if (route.query.q) {
    tab.value = 'accounts'
    accountQuery.value = String(route.query.q)
  }
  loadLoginCapability()
  loadPlatforms()
  loadAccounts()
  loadJobs()
  loadBatchStats()
  loadCampaigns()
  schedulePoll()
})

onBeforeUnmount(() => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
})
</script>

<style scoped>
.pub-page { gap: 18px; }
.pub-page > .mp-section-head { margin-bottom: 0; }


.kpi-strip {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
}
.kpi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 14px;
  border-radius: var(--mp-radius-sm);
  border: 1px solid var(--mp-border);
  background: var(--mp-surface);
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.kpi:hover, .kpi.active {
  border-color: #c9d8f8;
  box-shadow: var(--mp-shadow-sm);
}
.kpi-static { cursor: default; }
.kpi-static:hover { border-color: var(--mp-border); box-shadow: none; }
.kpi-value {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: var(--mp-text);
  line-height: 1;
}
.kpi-label { font-size: 12px; color: var(--mp-text-muted); }
.kpi.warn .kpi-value { color: var(--mp-warning); }
.kpi.info .kpi-value { color: var(--mp-accent); }
.kpi.ok .kpi-value { color: var(--mp-success); }
.kpi.bad .kpi-value { color: var(--mp-danger); }

.batch-alert { border-radius: var(--mp-radius-sm); }

.pub-tabs :deep(.el-tabs__header) { margin-bottom: 16px; }
.pub-tabs :deep(.el-tabs__nav-wrap::after) { height: 1px; background: var(--mp-border); }
.tab-count {
  margin-left: 4px;
  font-size: 12px;
  color: var(--mp-text-muted);
  font-weight: 500;
}
.tab-count.warn { color: var(--mp-warning); }

.accounts-layout {
  display: grid;
  grid-template-columns: 168px minmax(0, 1fr);
  gap: 16px;
  align-items: start;
}

.plat-rail {
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  background: var(--mp-surface);
  border: 1px solid var(--mp-border);
  border-radius: var(--mp-radius);
}
.plat-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 10px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: var(--mp-text-secondary);
  text-align: left;
}
.plat-item:hover { background: var(--mp-surface-muted); }
.plat-item.active {
  background: var(--mp-accent-soft);
  color: var(--mp-accent);
  font-weight: 600;
}
.plat-name { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; }
.plat-count { font-size: 12px; color: var(--mp-text-muted); }
.plat-item.active .plat-count { color: var(--mp-accent); }
.api-tag { transform: scale(0.9); }

.list-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
}
.status-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  border: 1px solid var(--mp-border);
  background: var(--mp-surface);
  font: inherit;
  font-size: 12.5px;
  color: var(--mp-text-secondary);
  cursor: pointer;
}
.chip.active {
  border-color: #c9d8f8;
  background: var(--mp-accent-soft);
  color: var(--mp-accent);
  font-weight: 600;
}
.chip-n { color: var(--mp-text-muted); font-size: 12px; }

.account-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
}

.acc-card {
  background: var(--mp-surface);
  border: 1px solid var(--mp-border);
  border-radius: var(--mp-radius);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.acc-card:hover {
  border-color: #d0daf0;
  box-shadow: var(--mp-shadow-sm);
}
.acc-card.disabled { opacity: 0.55; }

.acc-card-top {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.acc-card-id { flex: 1; min-width: 0; }
.acc-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--mp-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.acc-name-link {
  color: inherit;
  text-decoration: none;
}
.acc-name-link:hover {
  color: var(--el-color-primary);
  text-decoration: underline;
}
.acc-name.sm { font-size: 13px; }
.acc-meta {
  margin-top: 2px;
  font-size: 12px;
  color: var(--mp-text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}
.acc-meta .mono {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acc-uid {
  margin-top: 2px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11px;
  color: var(--mp-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dot { opacity: 0.5; }

.acc-card-status { display: flex; flex-wrap: wrap; gap: 6px; }

.acc-card-phone {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--mp-surface-muted);
  min-height: 34px;
}
.acc-card-phone .mode-pill {
  font-size: 12px;
  padding: 1px 8px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid transparent;
}
.acc-card-phone .mode-pill.auto { color: #18a058; background: rgba(24,160,88,.12); border-color: rgba(24,160,88,.35); }
.acc-card-phone .mode-pill.manual { color: #909399; background: rgba(144,147,153,.12); border-color: rgba(144,147,153,.3); }

.bind-hint { font-size: 12px; color: var(--mp-text-muted); line-height: 1.6; }

.acc-card-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  padding: 10px;
  border-radius: 8px;
  background: var(--mp-surface-muted);
}
.stat { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.stat-k { font-size: 11px; color: var(--mp-text-muted); }
.stat-v {
  font-size: 13px;
  font-weight: 600;
  color: var(--mp-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.acc-card-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.campaign-strip {
  background: var(--mp-surface);
  border: 1px solid var(--mp-border);
  border-radius: var(--mp-radius);
  padding: 14px 16px;
  margin-bottom: 14px;
}
.campaign-strip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}
.section-label { font-size: 13px; font-weight: 600; color: var(--mp-text); }
.campaign-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 10px;
}
.campaign-card {
  border: 1px solid var(--mp-border);
  border-radius: 8px;
  padding: 12px;
  background: var(--mp-surface-muted);
}
.campaign-card.active {
  border-color: #c9d8f8;
  background: var(--mp-accent-soft);
}
.campaign-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.campaign-stats {
  display: flex;
  gap: 10px;
  margin-top: 8px;
  font-size: 12px;
  color: var(--mp-text-secondary);
}
.campaign-stats .ok { color: var(--mp-success); }
.campaign-stats .warn { color: var(--mp-warning); }
.campaign-stats .bad { color: var(--mp-danger); }
.campaign-stats .info { color: var(--mp-accent); }
.campaign-actions { margin-top: 8px; display: flex; gap: 4px; }

.jobs-toolbar .poll-hint {
  margin-left: auto;
  font-size: 12px;
  color: var(--mp-text-muted);
}
.job-panel { overflow: hidden; }
.job-panel :deep(.el-table) { --el-table-header-bg-color: var(--mp-surface-muted); }
.job-title { font-size: 13.5px; font-weight: 550; color: var(--mp-text); }
.job-sub { font-size: 12px; color: var(--mp-text-muted); }
.acc-inline { display: flex; align-items: center; gap: 8px; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size: 12px; }
.muted { color: var(--mp-text-muted); }
.warn { color: var(--mp-warning); }
.hint { font-size: 12px; color: var(--mp-text-muted); margin-top: 6px; }

@media (max-width: 1100px) {
  .kpi-strip { grid-template-columns: repeat(4, minmax(0, 1fr)); }
  .accounts-layout { grid-template-columns: 1fr; }
  .plat-rail {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .kpi-strip {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }
  .kpi {
    padding: 10px;
  }
  .kpi-value { font-size: 16px; }
  .kpi-label { font-size: 11px; }
  .accounts-layout {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .plat-rail {
    display: flex;
    flex-direction: row;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    gap: 6px;
    padding-bottom: 4px;
  }
  .plat-item {
    flex: 0 0 auto;
    white-space: nowrap;
  }
  .account-grid {
    grid-template-columns: 1fr !important;
  }
  .list-toolbar {
    flex-direction: column;
    align-items: stretch !important;
  }
  .list-toolbar .el-input {
    width: 100% !important;
  }
  .status-chips {
    flex-wrap: wrap;
  }
}
</style>
